import React, { useEffect, useRef, useState } from "react";
import { usePlayer } from "@empirica/core/player/classic/react";
import { Button } from "./Button.jsx";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import { useChatAutoScroll } from "../utils/useChatAutoScroll.js";
import { TaskDetailsPanel } from "./TaskDetailsPanel.jsx";
import { POST_PEER_REFLECTION_KEYS } from "./ReflectionActivity.jsx";
import {
  getNicknameFromId,
  getAnimalFromId,
  getAnimalEmoji,
} from "./Avatar.jsx";

// askall variant of the post-peer reflection activity. Both arms target all
// three rationales (the participant's own answer plus the two synthetic
// peers') instead of the participant's answer alone. The Socratic arm runs
// three sequential mini-conversations, one rationale each, gated by a
// "Discussion done" click; the Template arm shows the same two questions
// repeated for each of the three rationales, six boxes in total. The askall
// conditions have no post-peer estimate stage, so the own answer under
// discussion is the participant's initial estimate, and the order of the
// three discussions (own answer included) is randomized once per participant
// by the stage and passed in as ordered entries. The existing components in
// ReflectionActivity.jsx are untouched; only the askall conditions
// (postPeerAskAll: true) render these.

// The askall reflection stage runs on a doubled clock (three conversations in
// place of one); keep in sync with the 840-second postPeerReflection duration
// in server/src/callbacks.js.
const ASKALL_REFLECTION_WAITING_TIME_SECONDS = 840;

function calculateAskAllRemainingTime(anchorAt) {
  if (!anchorAt) {
    return ASKALL_REFLECTION_WAITING_TIME_SECONDS;
  }
  const elapsed = Math.floor((Date.now() - new Date(anchorAt)) / 1000);
  return Math.max(ASKALL_REFLECTION_WAITING_TIME_SECONDS - elapsed, 0);
}

function getPeerNickname(peer) {
  return peer?.name || getNicknameFromId(peer?.id);
}

// Compact card showing the one rationale a window or block is about, matching
// the styling of the cards in PeerRationalesPanel.
function FocalPeerCard({ peer, formatBelief }) {
  if (!peer) {
    return null;
  }
  const nickname = getPeerNickname(peer);
  const animal = peer?.assignedAnimal || getAnimalFromId(peer?.id);
  const emoji = getAnimalEmoji(animal);
  const rationaleText =
    String(peer?.rationaleText || "").trim() ||
    "This earlier participant response is unavailable.";
  const estimate = peer?.initialBelief;
  const hasEstimate =
    estimate !== undefined && estimate !== null && String(estimate).trim() !== "";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
      <div className="mb-3 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-white shadow flex items-center justify-center text-xl">
          <span aria-hidden>{emoji}</span>
        </div>
        <div>
          <div className="font-semibold text-gray-900">{nickname}</div>
          {hasEstimate && (
            <div className="text-sm text-gray-600">
              Estimate:{" "}
              <span className="font-medium">{formatBelief(estimate)}</span>
            </div>
          )}
        </div>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
        {rationaleText}
      </p>
    </div>
  );
}

// Builds the window list from the ordered entries the stage passes in. The
// order over the three discussions (own answer included) is randomized once
// per participant and stored under postPeerAskAllWindowOrder by the stage.
function buildAskAllWindows(entries = []) {
  return entries.map((entry, position) => ({
    index: position + 1,
    peer: entry?.kind === "peer" ? entry.peer : null,
  }));
}

export function ReflectionAssistantChatAskAll({
  next,
  predictionForOpener,
  taskConfig,
  keys = POST_PEER_REFLECTION_KEYS,
  entries = [],
  ownAnswer = "",
  formatBelief = (value) => String(value ?? "").trim(),
  contextPanel = null,
  beliefReminder = null,
}) {
  const player = usePlayer();
  const windows = buildAskAllWindows(entries);
  const windowCount = windows.length;

  // Resume support: the first window without a recorded done-click is active.
  const initialActiveIndex = (() => {
    const progress = player.get("postPeerAskAllProgress") || {};
    const firstOpen = windows.find((w) => !progress[`window${w.index}DoneAt`]);
    return firstOpen ? firstOpen.index : windowCount;
  })();

  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [inputText, setInputText] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [messages, setMessages] = useState([]);
  const [expectingAiResponse, setExpectingAiResponse] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(
    calculateAskAllRemainingTime(player.get(keys.countdownAnchor))
  );
  const [forceScrollKey, setForceScrollKey] = useState(0);
  const openerTriggeredRef = useRef({});
  const finishedRef = useRef(false);

  const windowMessages = (index) =>
    messages.filter((m) => Number(m?.meta?.askAllWindow) === index);
  const userMessageCount = (index) =>
    windowMessages(index).filter(
      (m) => !m?.meta?.isAI && m?.role === "user" && m?.subjectId === player.id
    ).length;

  const activeWindow = windows.find((w) => w.index === activeIndex) || windows[0];
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    handleScroll,
  } = useChatAutoScroll({
    itemCount: windowMessages(activeIndex).length,
    typingVisible: isAiTyping,
    forceScrollKey,
  });

  // Listen for AI responses from the server (same pattern as the base chat).
  useEffect(() => {
    const storedMessages = player.get(keys.messages) || [];
    const currentMessageIds = messages.map((m) => m.id);
    const newMessages = storedMessages.filter(
      (msg) => !currentMessageIds.includes(msg.id)
    );
    if (newMessages.length > 0) {
      setMessages((prev) => [...prev, ...newMessages]);
      if (newMessages.some((msg) => msg?.meta?.isAI)) {
        setIsAiTyping(false);
        setExpectingAiResponse(false);
      }
    }
  }, [player.get(keys.messages), messages]);

  // Stage countdown with auto-advance, mirroring the base chat but on the
  // doubled askall clock.
  useEffect(() => {
    const timer = setInterval(() => {
      const anchorAt = player.get(keys.countdownAnchor);
      if (!anchorAt) {
        player.set(keys.countdownAnchor, Date.now());
      }
      const remaining = calculateAskAllRemainingTime(anchorAt);
      if (remaining <= 1) {
        console.log("[REFLECTION-AI][ASKALL] Time limit reached, auto-advancing");
        finishActivity({ autoAdvanced: true });
        return;
      }
      setTimeRemaining(remaining);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Record which window the server should build the prompt for. Written
  // before every trigger so the handler never sees a stale window.
  const publishActiveWindow = (windowObj) => {
    player.set("postPeerAskAllActiveWindow", {
      index: windowObj.index,
      peerId: windowObj.peer?.id || null,
      at: Date.now(),
    });
  };

  // Trigger the opener for a window the first time it becomes active.
  useEffect(() => {
    if (!predictionForOpener || !activeWindow) {
      return;
    }
    if (openerTriggeredRef.current[activeWindow.index]) {
      return;
    }
    const stored = (player.get(keys.messages) || []).filter(
      (m) => Number(m?.meta?.askAllWindow) === activeWindow.index
    );
    if (stored.length > 0) {
      openerTriggeredRef.current[activeWindow.index] = true;
      return;
    }
    openerTriggeredRef.current[activeWindow.index] = true;
    setIsAiTyping(true);
    setExpectingAiResponse(true);
    publishActiveWindow(activeWindow);
    setTimeout(() => {
      player.set(keys.aiTrigger, Date.now());
      console.log(
        "[REFLECTION-AI][ASKALL] Triggered opener for window",
        activeWindow.index
      );
    }, 60);
  }, [activeIndex, predictionForOpener]);

  const handleSendMessage = () => {
    if (!inputText.trim() || expectingAiResponse) return;

    const userMessage = {
      id: `user-w${activeIndex}-${Date.now()}`,
      text: inputText.trim(),
      createdAt: Date.now(),
      meta: {
        isAI: false,
        askAllWindow: activeIndex,
        askAllFocus: activeWindow?.peer ? String(activeWindow.peer.id) : "own",
      },
      role: "user",
      subjectId: player.id,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setExpectingAiResponse(true);
    setIsAiTyping(true);
    setForceScrollKey((prev) => prev + 1);

    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: keys.chatIntegrityFieldId,
      text: userMessage.text,
      phase: keys.integrityPhase,
      stageId: keys.integrityStageId,
      stageName: keys.chatIntegrityStageName,
    });

    const existingMessages = player.get(keys.messages) || [];
    const updatedMessages = [
      ...existingMessages,
      { ...userMessage, stageId: keys.chatMessageStageId, playerId: player.id },
    ];
    player.set(keys.messages, updatedMessages);
    publishActiveWindow(activeWindow);

    setTimeout(() => {
      player.set(keys.aiTrigger, Date.now());
    }, 60);
  };

  const recordWindowDone = (index) => {
    const progress = player.get("postPeerAskAllProgress") || {};
    const updated = { ...progress, [`window${index}DoneAt`]: Date.now() };
    player.set("postPeerAskAllProgress", updated);
    return updated;
  };

  const finishActivity = ({ autoAdvanced = false } = {}) => {
    if (finishedRef.current) {
      return;
    }
    finishedRef.current = true;
    const progress = player.get("postPeerAskAllProgress") || {};
    player.set("postPeerAskAllProgress", {
      ...progress,
      ...(autoAdvanced ? { autoAdvancedAt: Date.now() } : {}),
      completedAt: Date.now(),
    });
    player.set(keys.assistantCompleted, true);
    player.set(keys.assistantCompletedAt, Date.now());
    const storedMessages = player.get(keys.messages) || [];
    savePlayerMeta({
      player,
      name: keys.messages,
      value: storedMessages,
      debugTag: keys.chatMessagesDebugTag,
    });
    next();
  };

  const handleWindowDone = (index) => {
    const isLast = index === windows[windows.length - 1].index;
    const confirmed = window.confirm(
      isLast
        ? "You are about to finish the last discussion. Please make sure you have finished discussing before continuing."
        : "You are about to finish this discussion and move on to the next answer. You will not be able to return to this discussion."
    );
    if (!confirmed) {
      return;
    }
    recordWindowDone(index);
    if (isLast) {
      finishActivity();
      return;
    }
    const nextWindow = windows.find((w) => w.index > index);
    if (nextWindow) {
      setActiveIndex(nextWindow.index);
      setForceScrollKey((prev) => prev + 1);
    }
  };

  const renderMessageBubble = (message) => (
    <div
      key={message.id}
      className={`mb-4 ${message.meta?.isAI ? "text-left" : "text-right"}`}
    >
      <div
        className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
          message.meta?.isAI
            ? "bg-blue-100 text-blue-900"
            : "bg-green-100 text-green-900"
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold">
            {message.meta?.isAI ? "🤖 AI Assistant" : "👤 You"}
          </span>
          <span className="text-xs opacity-70">
            {new Date(message.createdAt).toLocaleTimeString()}
          </span>
        </div>
        <p className="text-sm">{message.text}</p>
      </div>
    </div>
  );

  const windowTitle = (windowObj) =>
    windowObj.peer
      ? `${getPeerNickname(windowObj.peer)}'s answer`
      : "Your answer";

  const progress = player.get("postPeerAskAllProgress") || {};

  return (
    <div className="h-full min-h-0 w-full max-w-4xl mx-auto p-6 flex flex-col">
      <h2 className="text-2xl font-bold mb-6 shrink-0">
        {taskConfig?.aiChatTitle || "AI Assistant Discussion"}
      </h2>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="space-y-6">
          {taskConfig?.integrityNotice && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-900">{taskConfig.integrityNotice}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-gray-700">
                You will have {windowCount} short discussions with the AI
                assistant, one for each answer: your own and the two other
                participants'. Finish each discussion, then click "Discussion
                done" to move to the next one. It will take 5-10 seconds for
                the AI assistant to respond.
              </p>
              <div className="text-right shrink-0">
                <div className="text-sm text-gray-500">Time Remaining</div>
                <div
                  className={`text-xl font-mono font-semibold ${
                    timeRemaining < 60 ? "text-red-600" : "text-gray-900"
                  }`}
                >
                  {Math.floor(timeRemaining / 60)}:
                  {(timeRemaining % 60).toString().padStart(2, "0")}
                </div>
              </div>
            </div>
          </div>

          {contextPanel}

          <div className="bg-white rounded-lg shadow-md p-6">
            <TaskDetailsPanel
              taskConfig={taskConfig}
              open={showTaskDetails}
              onToggle={() => setShowTaskDetails((prev) => !prev)}
              scrollable
            />
          </div>

          {beliefReminder}

          {windows.map((windowObj, position) => {
            const isDone = Boolean(progress[`window${windowObj.index}DoneAt`]);
            const isActive = windowObj.index === activeIndex && !isDone;
            const isLocked = !isDone && !isActive;
            const bubbles = windowMessages(windowObj.index);
            const canFinish =
              isActive &&
              userMessageCount(windowObj.index) > 0 &&
              !expectingAiResponse;
            const isLast = windowObj.index === windows[windows.length - 1].index;

            return (
              <div
                key={windowObj.index}
                className={`bg-white rounded-lg shadow-md p-6 ${
                  isLocked ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold">
                    Discussion {position + 1} of {windowCount}:{" "}
                    {windowTitle(windowObj)}
                  </h3>
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      isDone
                        ? "bg-green-100 text-green-800"
                        : isActive
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isDone ? "Completed" : isActive ? "In progress" : "Locked"}
                  </span>
                </div>

                {windowObj.peer && !isLocked && (
                  <FocalPeerCard peer={windowObj.peer} formatBelief={formatBelief} />
                )}

                {!windowObj.peer &&
                  !isLocked &&
                  String(ownAnswer ?? "").trim() !== "" && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                      <p className="text-sm text-gray-800">
                        Your answer:{" "}
                        <span className="font-semibold">
                          {formatBelief(ownAnswer)}
                        </span>{" "}
                        (the estimate you gave earlier).
                      </p>
                    </div>
                  )}

                {isLocked ? (
                  <p className="text-sm text-gray-500">
                    Finish the discussion above to unlock this one.
                  </p>
                ) : (
                  <>
                    <div
                      ref={isActive ? messagesContainerRef : undefined}
                      onScroll={isActive ? handleScroll : undefined}
                      className={`overflow-y-auto mb-4 border rounded-lg p-4 bg-gray-50 ${
                        isActive ? "h-72" : "max-h-48"
                      }`}
                    >
                      {bubbles.map(renderMessageBubble)}
                      {isActive && isAiTyping && (
                        <div className="text-left mb-4">
                          <div className="inline-block px-4 py-2 rounded-lg bg-blue-100 text-blue-900">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold">
                                🤖 AI Assistant
                              </span>
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                                <div
                                  className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.1s" }}
                                ></div>
                                <div
                                  className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.2s" }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {isActive && <div ref={messagesEndRef} />}
                    </div>

                    {isActive && (
                      <>
                        <div className="flex gap-2 shrink-0 mb-4">
                          <input
                            type="text"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-empirica-500 focus:border-empirica-500"
                            placeholder="Share your thoughts with the AI assistant..."
                            value={inputText}
                            data-integrity-field={keys.chatIntegrityFieldId}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={(e) =>
                              e.key === "Enter" && handleSendMessage()
                            }
                            disabled={expectingAiResponse}
                          />
                          <Button
                            handleClick={handleSendMessage}
                            disabled={!inputText.trim() || expectingAiResponse}
                            className="px-4 py-2 bg-empirica-500 hover:bg-empirica-600 disabled:opacity-50"
                          >
                            Send
                          </Button>
                        </div>

                        {expectingAiResponse && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-yellow-800">
                              <strong>Note:</strong> If you do not receive an AI
                              response within 20 seconds, please refresh the
                              browser and resend your message.
                            </p>
                          </div>
                        )}

                        <div className="flex justify-center">
                          <Button
                            handleClick={() => handleWindowDone(windowObj.index)}
                            disabled={!canFinish}
                            className="px-8 py-3 bg-green-500 hover:bg-green-600"
                          >
                            {isLast ? "Discussion done — Continue" : "Discussion done"}
                          </Button>
                        </div>
                        {!canFinish && (
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            Please discuss this answer with the AI assistant
                            before finishing this discussion.
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-600">
              Please finish a meaningful discussion of each answer. The stage
              advances automatically after {Math.round(
                ASKALL_REFLECTION_WAITING_TIME_SECONDS / 60
              )}{" "}
              minutes if you have not finished all discussions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReflectionTemplateQuestionnaireAskAll({
  next,
  taskConfig,
  keys = POST_PEER_REFLECTION_KEYS,
  entries = [],
  ownAnswer = "",
  formatBelief = (value) => String(value ?? "").trim(),
  contextPanel = null,
  beliefReminder = null,
  // nocounter conditions (postPeerNoCounter: true) drop the counter-argument
  // question from every block, leaving one box per rationale instead of two.
  // The storage keys and the response shape are unchanged: oppositeReason and
  // peerNOppositeReason are written as empty strings, the way reasonMatters
  // already is in this component.
  noCounter = false,
}) {
  const player = usePlayer();
  const [showTaskDetails, setShowTaskDetails] = useState(false);

  // The own-answer block reuses the condition's aligned questions verbatim so
  // it stays comparable with the earlier waves' two-box instrument; the peer
  // blocks repeat the same pair with the referent swapped to the peer. Blocks
  // render in the randomized entry order; the peer1/peer2 storage keys stay
  // tied to the peer-review display order, so a key names the same peer no
  // matter where that peer's block lands for a given participant.
  const taskTemplateQuestionnaire = taskConfig?.templateQuestionnaire || {};
  const ownMainQuestion =
    taskTemplateQuestionnaire.mainReason?.question ||
    "What is the main reason behind your prediction?";
  const ownOppositeQuestion =
    taskTemplateQuestionnaire.oppositeReason?.question ||
    "What would someone who disagrees say?";

  const displayOrderedPeers = entries
    .filter((entry) => entry.kind === "peer" && entry.peer)
    .map((entry) => entry.peer)
    .sort(
      (a, b) => (Number(a?.displayPosition) || 0) - (Number(b?.displayPosition) || 0)
    );
  const peerKeyIndexById = {};
  displayOrderedPeers.forEach((peer, position) => {
    peerKeyIndexById[String(peer.id)] = position + 1;
  });

  const makePeerBlock = (peer) => {
    const nickname = getPeerNickname(peer);
    const keyIndex = peerKeyIndexById[String(peer.id)];
    return {
      blockId: `peer${keyIndex}`,
      peer,
      title: `${nickname}'s answer`,
      questions: [
        {
          questionId: `peer${keyIndex}MainReason`,
          question: `In your own words, what is the main reason behind ${nickname}'s prediction?`,
          placeholder: `Please describe ${nickname}'s main reason in your own words.`,
        },
        ...(noCounter
          ? []
          : [
              {
                questionId: `peer${keyIndex}OppositeReason`,
                question: `What would someone who disagrees with ${nickname} say?`,
                placeholder: `Please describe the strongest argument against ${nickname}'s prediction.`,
              },
            ]),
      ],
    };
  };

  const ownBlock = {
    blockId: "own",
    peer: null,
    title: "Your answer",
    questions: [
      {
        questionId: "mainReason",
        question: ownMainQuestion,
        placeholder:
          taskTemplateQuestionnaire.mainReason?.placeholder ||
          "Please describe your main reason.",
      },
      ...(noCounter
        ? []
        : [
            {
              questionId: "oppositeReason",
              question: ownOppositeQuestion,
              placeholder:
                taskTemplateQuestionnaire.oppositeReason?.placeholder ||
                "Please describe the strongest argument against your prediction.",
            },
          ]),
    ],
  };

  const blocks = entries
    .map((entry) =>
      entry.kind === "own" ? ownBlock : entry.peer ? makePeerBlock(entry.peer) : null
    )
    .filter(Boolean);

  const allQuestions = blocks.flatMap((block) => block.questions);
  const [answers, setAnswers] = useState(() => {
    const stored = player.get(keys.templateQuestionnaire) || {};
    const initial = {};
    allQuestions.forEach(({ questionId }) => {
      initial[questionId] = String(stored[questionId] || "");
    });
    return initial;
  });

  const trimmed = {};
  allQuestions.forEach(({ questionId }) => {
    trimmed[questionId] = String(answers[questionId] || "").trim();
  });
  const hasCompletedAllQuestions = allQuestions.every(({ questionId }) =>
    Boolean(trimmed[questionId])
  );

  const handleContinue = () => {
    if (!hasCompletedAllQuestions) {
      alert("Please answer all required questions before continuing.");
      return;
    }

    const timestamp = Date.now();
    const responses = {
      askAll: true,
      askAllNoCounter: noCounter,
      mainReason: trimmed.mainReason,
      reasonMatters: "",
      oppositeReason: noCounter ? "" : trimmed.oppositeReason,
      ownEstimate: String(ownAnswer ?? ""),
      askAllBlockOrder: entries.map((entry) =>
        entry.kind === "own" ? "own" : String(entry.peer?.id || "")
      ),
      completedAt: timestamp,
    };
    displayOrderedPeers.forEach((peer, position) => {
      const index = position + 1;
      responses[`peer${index}Id`] = peer?.id || "";
      responses[`peer${index}Name`] = getPeerNickname(peer);
      responses[`peer${index}Estimate`] = String(peer?.initialBelief ?? "");
      responses[`peer${index}MainReason`] = trimmed[`peer${index}MainReason`];
      responses[`peer${index}OppositeReason`] = noCounter
        ? ""
        : trimmed[`peer${index}OppositeReason`];
    });

    player.set(keys.templateMainReason, responses.mainReason);
    player.set(keys.templateReasonMatters, "");
    player.set(keys.templateOppositeReason, responses.oppositeReason);
    player.set(keys.templateQuestionnaireCompletedAt, timestamp);
    player.set(keys.templateQuestionnaire, responses);

    const templateMessages = [];
    blocks.forEach((block) => {
      block.questions.forEach(({ questionId, question }, questionIndex) => {
        templateMessages.push({
          id: `template-askall-${questionId}-${timestamp}`,
          role: "user",
          text: `Question: ${question}\nAnswer: ${trimmed[questionId]}`,
          createdAt: timestamp + templateMessages.length,
          subjectId: player.id,
          stageId: keys.templateMessageStageId,
          playerId: player.id,
          meta: {
            isAI: false,
            templateQuestionnaire: true,
            questionId,
            askAllBlock: block.blockId,
            askAllQuestionIndex: questionIndex,
          },
        });
        recordSubmittedIntegrityText({
          playerId: player.id,
          fieldId: `${keys.templateIntegrityFieldPrefix}-${questionId}`,
          text: trimmed[questionId],
          phase: keys.integrityPhase,
          stageId: keys.integrityStageId,
          stageName: keys.templateIntegrityStageName,
        });
      });
    });

    const existingMessages = (player.get(keys.messages) || []).filter(
      (msg) => !msg?.meta?.templateQuestionnaire
    );
    const updatedMessages = [...existingMessages, ...templateMessages];
    player.set(keys.messages, updatedMessages);

    savePlayerMeta({
      player,
      name: keys.templateQuestionnaire,
      value: responses,
      debugTag: keys.templateResponsesDebugTag,
    });
    savePlayerMeta({
      player,
      name: keys.messages,
      value: updatedMessages,
      debugTag: keys.templateMessagesDebugTag,
    });

    next();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">Follow-up Questionnaire</h2>

      {taskConfig?.integrityNotice && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-900">{taskConfig.integrityNotice}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <p className="text-gray-700">
          Before you give your final answer, please answer the{" "}
          {noCounter ? "question" : "questions"} below about each of the three
          answers: your own and the two other participants'.
        </p>
      </div>

      {contextPanel}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <TaskDetailsPanel
          taskConfig={taskConfig}
          open={showTaskDetails}
          onToggle={() => setShowTaskDetails((prev) => !prev)}
        />
      </div>

      {beliefReminder}

      {blocks.map((block, position) => (
        <div key={block.blockId} className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            Part {position + 1} of {blocks.length}: {block.title}
          </h3>
          {block.peer && (
            <FocalPeerCard peer={block.peer} formatBelief={formatBelief} />
          )}
          {!block.peer && String(ownAnswer ?? "").trim() !== "" && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
              <p className="text-sm text-gray-800">
                Your answer:{" "}
                <span className="font-semibold">{formatBelief(ownAnswer)}</span>{" "}
                (the estimate you gave earlier).
              </p>
            </div>
          )}
          {block.questions.map(({ questionId, question, placeholder }) => (
            <div key={questionId} className="mb-4 last:mb-0">
              <label className="block text-base font-semibold mb-2">
                {question}
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md p-3 min-h-[100px]"
                value={answers[questionId] || ""}
                data-integrity-field={`${keys.templateIntegrityFieldPrefix}-${questionId}`}
                onChange={(e) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [questionId]: e.target.value,
                  }))
                }
                placeholder={placeholder}
              />
            </div>
          ))}
        </div>
      ))}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center">
          <Button
            handleClick={handleContinue}
            className="px-8 py-3 bg-green-500 hover:bg-green-600"
            disabled={!hasCompletedAllQuestions}
          >
            Continue
          </Button>
        </div>
        {!hasCompletedAllQuestions && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Please answer all required questions before continuing.
          </p>
        )}
      </div>
    </div>
  );
}
